import { useState, useMemo, useEffect } from 'react';
import { SCUIGeneralDrawer } from '@/atomic/molecules';
import { SCUIGeneralDrawerHeader } from '@/atomic/molecules';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUIDateRangerV2 } from '@/atomic/atoms';
import MPPaymentOnDeliveryCommissionCommand from '@/services/command/mpPaymentOnDeliveryCommission/MPPaymentOnDeliveryCommission.services';
import Swal from 'sweetalert2';
import moment from 'moment';
import { format } from 'date-fns';
import { MPODC } from '../mpPaymentOnDeliveryCommission.classes';

interface FeeRowItem {
    commission: number;
    maximumPrice: number;
    minimumPrice: number;
    shippingCompanyId: number;
    startDate: string | Date | null;
    expirationDate: string | Date | null;
}

type Props = {
    open: boolean;
    onClose: () => void;
    companyOptions: any;
    initialData: any;
    setIsLoading: (isLoading: boolean) => void;
    onSaved: () => void;
    filterCompanyId: number;
};

export const EditDrawer = ({
    open,
    onClose,
    companyOptions,
    initialData,
    setIsLoading,
    onSaved,
    filterCompanyId,
}: Props) => {
    const isEditMode = Boolean(initialData);
    const { t } = useTranslation();

    const initialFormState: FeeRowItem = useMemo(
        () => ({
            commission: 0,
            maximumPrice: 0,
            minimumPrice: 0,
            shippingCompanyId: filterCompanyId || 0,
            startDate: format(new Date(), 'dd-MM-yyyy'),
            expirationDate: format(new Date(), 'dd-MM-yyyy'),
        }),
        []
    );

    const [formState, setFormState] = useState<FeeRowItem>(initialData || initialFormState);

    useEffect(() => {
        if (isEditMode && initialData) {
            const normalized: FeeRowItem = {
                shippingCompanyId: filterCompanyId || 0,
                commission: initialData.commission ?? 0,
                maximumPrice: initialData.maximumPrice ?? 0,
                minimumPrice: initialData.minimumPrice ?? 0,
                startDate: initialData.startDate ? format(new Date(initialData.startDate), 'dd-MM-yyyy') : '',
                expirationDate: initialData.expirationDate
                    ? format(new Date(initialData.expirationDate), 'dd-MM-yyyy')
                    : '',
            };
            setFormState(normalized);
        } else {
            setFormState(initialFormState);
        }
    }, [isEditMode, initialData, initialFormState]);

    const handleChangeCompany = (e: SelectChangeEvent<number>) => {
        const id = Number(e.target.value);
        setFormState((prev) => ({ ...prev, shippingCompanyId: id }));
    };

    const handleChangeField = (key: keyof FeeRowItem, value: any) => {
        setFormState((prev) => ({ ...prev, [key]: value }));
    };

    const isFormValid = () => {
        const min = Number(formState.minimumPrice);
        const max = Number(formState.maximumPrice);
        return Number(formState.shippingCompanyId) > -1 && Number.isFinite(min) && Number.isFinite(max) && max > min;
    };

    const toYYYYMMDDSlash = (val: string | Date | null | undefined): string | null => {
        if (!val) return null;
        if (val instanceof Date) return moment(val).format('YYYY/MM/DD');

        const s = String(val).trim();
        if (!s) return null;

        // Already ISO or other parseable string -> normalize
        const parsed = moment(s, ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY/MM/DD', moment.ISO_8601], true);
        if (parsed.isValid()) return parsed.format('YYYY/MM/DD');

        // Last-resort parse (non-strict)
        const fallback = moment(s);
        return fallback.isValid() ? fallback.format('YYYY/MM/DD') : null;
    };

    const handleSave = () => {
        setIsLoading(true);
        const payload = {
            ShippingCompanyId: Number(formState.shippingCompanyId),
            MinimumPrice: Number(formState.minimumPrice),
            MaximumPrice: Number(formState.maximumPrice),
            Commission: Number(formState.commission),
            StartDate: toYYYYMMDDSlash(formState.startDate),
            ExpirationDate: toYYYYMMDDSlash(formState.expirationDate),
        };

        MPPaymentOnDeliveryCommissionCommand.UpsertCodCommissionRange(payload)
            .then((res: any) => {
                if (!res.isSuccess) {
                    Swal.fire({
                        title: t('general.error'),
                        text: res.message,
                        icon: 'error',
                    });
                    return;
                }
                Swal.fire({
                    title: t('general.success'),
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1500,
                });
                onSaved();
                onClose();
                setFormState(initialFormState);
            })
            .catch(() => {
                Swal.fire({
                    title: t('general.error'),
                    text: t('general.anErrorOccured'),
                    icon: 'error',
                });
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <SCUIGeneralDrawer width="480px" open={open} onClose={onClose}>
            <SCUIGeneralDrawerHeader
                header={
                    isEditMode
                        ? t('mpPaymentOnDeliveryCommission.list.drawer.editTitle')
                        : t('mpPaymentOnDeliveryCommission.list.drawer.addTitle')
                }
                onClose={onClose}
            />
            <Box className={MPODC.codDrawerBody} sx={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box className={MPODC.codDrawerFields} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <FormControl className={MPODC.codDrawerFieldCompany} fullWidth>
                        <InputLabel sx={{ fontWeight: 600 }} id="cargo-company-label">
                            {t('mpPaymentOnDeliveryCommission.list.drawer.cargoCompany')}
                        </InputLabel>
                        <Select
                            className={MPODC.inputSelectDrawerCompany}
                            labelId="cargo-company-label"
                            value={initialData?.shippingCompanyId ?? formState?.shippingCompanyId ?? ''}
                            label={t('mpPaymentOnDeliveryCommission.list.drawer.cargoCompany')}
                            onChange={handleChangeCompany as any}
                            disabled
                        >
                            {companyOptions
                                .filter((opt: any) => opt.id !== -1)
                                .map((opt: any) => (
                                    <MenuItem key={opt.id} value={opt.id}>
                                        {opt.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <TextField
                        className={MPODC.inputTextDrawerMinPrice}
                        label={t('mpPaymentOnDeliveryCommission.list.drawer.minimumPrice')}
                        slotProps={{
                            inputLabel: { sx: { fontWeight: 600 } },
                            htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' },
                        }}
                        type="text"
                        value={formState?.minimumPrice ?? ''}
                        onChange={(e) => {
                            handleChangeField('minimumPrice', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];

                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        fullWidth
                    />

                    <TextField
                        className={MPODC.inputTextDrawerMaxPrice}
                        label={t('mpPaymentOnDeliveryCommission.list.drawer.maximumPrice')}
                        slotProps={{
                            inputLabel: { sx: { fontWeight: 600 } },
                            htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' },
                        }}
                        type="text"
                        value={formState?.maximumPrice ?? ''}
                        onChange={(e) => {
                            handleChangeField('maximumPrice', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
                            if (e.key === '.' && e.target.value?.includes('.')) {
                                e.preventDefault();
                                return;
                            }
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        fullWidth
                    />
                    <TextField
                        className={MPODC.inputTextDrawerCommission}
                        label={t('mpPaymentOnDeliveryCommission.list.drawer.commission')}
                        slotProps={{
                            inputLabel: { sx: { fontWeight: 600 } },
                            htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' },
                        }}
                        type="text"
                        value={formState?.commission ?? ''}
                        onChange={(e) => {
                            handleChangeField('commission', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
                            if (e.key === '.' && e.target.value?.includes('.')) {
                                e.preventDefault();
                                return;
                            }
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        fullWidth
                    />
                </Box>

                <Box className={MPODC.codDrawerDateSection} sx={{ width: '100%', pt: 3 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '16px', fontWeight: 600 }}>
                        {t('general.dateRange')}
                    </Typography>
                    <Box className={MPODC.codDrawerDateRangeWrap}>
                        <SCUIDateRangerV2
                            fromDate={formState?.startDate}
                            toDate={formState?.expirationDate}
                            setDateRange={(range: [string, string]) => {
                                setFormState((prev) => ({
                                    ...prev,
                                    startDate: range?.[0] || null,
                                    expirationDate: range?.[1] || null,
                                }));
                            }}
                            dateFormat="DD/MM/YYYY"
                            containerStyling={{ width: '100%' }}
                        />
                    </Box>
                </Box>
                <Box
                    className={MPODC.codDrawerActionsRow}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                    }}
                >
                    <SCUIButton
                        className={MPODC.inputButtonDrawerClear}
                        text={t('general.clear')}
                        onClick={() =>
                            isEditMode ? setFormState(initialData || initialFormState) : setFormState(initialFormState)
                        }
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                    />
                    <SCUIButton
                        className={MPODC.inputButtonDrawerSave}
                        text={t('mpPaymentOnDeliveryCommission.list.drawer.save')}
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        buttonType="button"
                        onClick={handleSave}
                        disabled={!isFormValid()}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};
